import React, { useState, useEffect } from "react";
import Message from "./Message";
import Sidebar from "../Sidebar";
import InternalHeader from "../InternalHeader";

const ChatApp = () => {
    const [isSidebarVisible, setIsSidebarVisible] = useState(true);
    const [currentUser, setCurrentUser] = useState(null); // Logged-in user (sender)
    const [internalUsers, setInternalUsers] = useState([]);
    const [externalUsers, setExternalUsers] = useState([]);
    const [receiverId, setReceiverId] = useState(""); // Selected receiver
    const [showNewMessageForm, setShowNewMessageForm] = useState(false);
    const [userType, setUserType] = useState(null); // 'internal' or 'external'

    // Fetch the current user's profile and determine user type
    useEffect(() => {
        const fetchProfile = async () => {
            try {
                // Fetch current user profile
                const profileResponse = await fetch("https://api.hgcdental.clinic/api/profile", {
                    credentials: "include",
                });

                if (!profileResponse.ok) {
                    throw new Error('Failed to fetch profile.');
                }

                const profile = await profileResponse.json();
                setCurrentUser(profile);

                // Fetch both internal and external users
                const [externalRes, internalRes] = await Promise.all([
                    fetch('https://api.hgcdental.clinic/api/externalusers'),
                    fetch('https://api.hgcdental.clinic/api/internalusers'),
                ]);

                if (!externalRes.ok || !internalRes.ok) {
                    throw new Error('Failed to fetch user lists.');
                }

                const externalUsers = await externalRes.json();
                const internalUsers = await internalRes.json();

                setExternalUsers(externalUsers);
                setInternalUsers(internalUsers);

                // Determine user type
                const isExternal = externalUsers.some(u => u.email === profile.email);
                const isInternal = internalUsers.some(u => u.email === profile.email);

                if (isExternal) setUserType('external');
                else if (isInternal) setUserType('internal');
                else setUserType(null);

            } catch (error) {
                console.error('Error fetching profile or user type:', error);
            }
        };

        fetchProfile();
    }, []);

    // Get the appropriate receiver list based on user type
    const getReceiverOptions = () => {
        if (userType === 'internal') {
            // Internal users can message both internal and external users
            return [
                ...internalUsers.map(user => ({ ...user, type: 'internal' })),
                ...externalUsers.map(user => ({ ...user, type: 'external' }))
            ];
        } else if (userType === 'external') {
            // External users can only message internal users
            return internalUsers.map(user => ({ ...user, type: 'internal' }));
        }
        return []; // Default empty array
    };

    const receiverOptions = getReceiverOptions();

    return (
        <div className={`appointments-page ${isSidebarVisible ? "sidebar-open" : "sidebar-closed"}`}>
            <InternalHeader moduleName="Messages" toggleSidebar={() => setIsSidebarVisible(!isSidebarVisible)} />
            <div className="appointments-container">
                <Sidebar isSidebarVisible={isSidebarVisible} />
                <div className="flex-div-1">
                    {/* Placeholder message */}
                    {!showNewMessageForm && (
                        <p className="placeholder-text">Click "New Message" to start a conversation.</p>
                    )}

                    {/* New Message Button */}
                    <button className="new-message-button" onClick={() => setShowNewMessageForm(!showNewMessageForm)}>
                        {showNewMessageForm ? "Cancel" : "New Message"}
                    </button>

                    {/* Render New Message Form */}
                    {showNewMessageForm && (
                        <div className="flex-div-1">
                            <div>
                                <label htmlFor="receiver">Select Receiver:</label>
                                <select
                                    id="receiver"
                                    value={receiverId}
                                    onChange={(e) => setReceiverId(e.target.value)}
                                >
                                    <option value="">Select Receiver</option>
                                    {receiverOptions.map((user) => (
                                        <option key={`${user.type}-${user._id}`} value={user._id}>
                                            {user.firstName} {user.lastName} ({user.type})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                {/* Render Message Component */}
                                {currentUser && receiverId ? (
                                    <Message senderId={currentUser.id} receiverId={receiverId} />
                                ) : (
                                    <p>Please select a receiver to start a conversation.</p>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ChatApp;